import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { cn } from "@groot/ui/lib/utils";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import { useEffect, useMemo, useRef, useState } from "react";

// Set worker path for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface SafePdfViewerProps {
  /** URL or data URI of the PDF to render. */
  pdfUrl: string;
  /** Extra classes for the scroll container. */
  className?: string;
}

/**
 * Renders a PDF via pdf.js (react-pdf) instead of a native browser viewer.
 *
 * The native viewer (used by <iframe>) executes embedded PDF JavaScript —
 * e.g. `app.alert(...)` — which surfaces as alert dialogs in the app.
 * pdf.js never executes embedded JS and the annotation layer is disabled,
 * so no interactive/alerting widgets are rendered either.
 */
export function SafePdfViewer({ pdfUrl, className }: SafePdfViewerProps) {
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  const options = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    [],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    setWidth(Math.floor(el.clientWidth));
    return () => ro.disconnect();
  }, []);

  // Reset state when the PDF changes
  useEffect(() => {
    setNumPages(0);
    setLoadError(false);
  }, [pdfUrl]);

  return (
    <div ref={containerRef} className={cn("h-full overflow-auto", className)}>
      <Document
        file={pdfUrl}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={() => setLoadError(true)}
        options={options}
        loading={
          <div className="flex justify-center p-8">
            <LoadingSpinner />
          </div>
        }
        error={<div className="p-8 text-center text-destructive">Failed to load PDF</div>}
      >
        {!loadError &&
          Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
            <div key={pageNumber} className="mb-4 last:mb-0 bg-white shadow-sm">
              <Page
                pageNumber={pageNumber}
                width={width > 0 ? width : undefined}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
      </Document>
    </div>
  );
}
